import { useState } from "react";
import { AcDialog, Button, Field, FormDialog, UrlBar } from "@cntrl/ui";
import { errorMessage } from "../../../app/hooks/apiError.js";
import { pushToast } from "../../../app/toasts.js";
import { PUBLIC_WEB_URL } from "../../../config/env.js";
import {
  useRotateVenueToken, useUpdateVenueLink, useVenueToken,
  type AdvanceLookView, type LookId,
} from "../../../app/hooks/useProduction.js";
import { advanceTitle, formatLinkDate } from "./advanceFormat.js";
import styles from "./AdvanceCatalogue.module.scss";

/**
 * LINK SETTINGS for one advance (ART-146, spec §6.4) — the url, its password, its expiry, Rotate and Copy,
 * all in one place because they are all facts about the same link. (DELETE lives on the recall bar beside the
 * button that opens this modal — destroying the whole advance is not a fact about its link.)
 *
 * **The link is born HERE.** An advance starts with no link at all ("no link exists until you set something
 * — set anything below to mint one"), so the save path mints on first use rather than making a band press a
 * separate Generate first. Rotate mints too, which is what the classic surface has always done.
 *
 * Every destructive op is confirmed through `AcDialog`, never a hand-rolled dialog, and never a bare click:
 * Rotate permanently kills a url venues are already holding.
 */
export function AdvanceLinkModal(props: {
  look: AdvanceLookView;
  onClose: () => void;
}) {
  const { look } = props;
  const lookId: LookId = look.lookId ?? null;
  const q = useVenueToken(lookId);
  const save = useUpdateVenueLink(lookId);
  const rotate = useRotateVenueToken(lookId);

  const [password, setPassword] = useState("");
  // The expiry input is seeded from the server ONCE and then owned by the form, so a background refetch
  // cannot pull a date out from under someone mid-edit.
  const [expires, setExpires] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<"rotate" | null>(null);

  const token = q.data;
  const storedExpiry = token?.expiresOn ?? "";
  const expiryValue = expires ?? storedExpiry;
  const url = token?.publicPath ? `${PUBLIC_WEB_URL}${token.publicPath}` : null;

  const dirty = password.trim().length > 0 || expiryValue !== storedExpiry;
  // Both fields are OPTIONAL (ART-221): on a link-less advance Create link must be enabled with neither set, so
  // a bare link can be minted — gating it on `dirty` made that impossible. It bites hardest on a lapsed band,
  // whose only two routes to `dirty` are the fields that 402. Once a link exists, Save still needs a change.
  const canSubmit = token?.hasToken ? dirty : true;

  const onCopy = async () => {
    if (!url) return;
    try { await navigator.clipboard.writeText(url); pushToast("Advance link copied.", "success"); }
    catch { pushToast("Couldn't copy the link.", "error"); }
  };

  const apply = (body: Parameters<typeof save.mutate>[0], success: string) =>
    save.mutate(body, {
      onSuccess: () => { setPassword(""); setExpires(null); pushToast(success, "success"); },
      onError: (e) => pushToast(
        // A 402 here is the tier gate, and it fires only on a SET — the message has to say WHY rather than
        // read as a failure, because the band can still clear and can still buy.
        errorMessage(e, "Couldn't update the link."), "error"),
    });

  const onSave = () => {
    const next = expiryValue.trim();
    apply(
      {
        ...(password.trim() ? { password: password.trim() } : {}),
        ...(next ? { expiresOn: next } : storedExpiry ? { clearExpiry: true } : {}),
      },
      token?.hasToken ? "Link settings saved." : "Advance link created.",
    );
  };

  const onRotate = () =>
    rotate.mutate(undefined, {
      onSuccess: () => { setConfirm(null); pushToast("Link rotated — the old url no longer works.", "success"); },
      onError: (e) => pushToast(errorMessage(e, "Couldn't rotate the link."), "error"),
    });

  return (
    <>
      <FormDialog
        title={advanceTitle(look)}
        onClose={props.onClose}
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={props.onClose}>Done</Button>
            <Button onClick={onSave} disabled={!canSubmit || save.isPending}>
              {save.isPending ? "Saving…" : token?.hasToken ? "Save" : "Create link"}
            </Button>
          </>
        }
      >
        {url && (
          <UrlBar
            url={url} copyLabel="Copy link" onCopy={() => void onCopy()}
            actions={
              <Button variant="ghost" size="xs" onClick={() => setConfirm("rotate")} disabled={rotate.isPending}>
                {rotate.isPending ? "Rotating…" : "Rotate"}
              </Button>
            }
          />
        )}

        {token?.expired && (
          <p className={styles.expiredNote} role="status">
            This public link expired {formatLinkDate(token.expiresOn)} — venues get a &ldquo;link expired&rdquo;
            page. The advance itself is safe here and always recallable.
          </p>
        )}

        <Field
          label={token?.hasPassword ? "Password (set)" : "Password (optional)"}
          // A minted gate typed on our origin — set once on one link, never read back, never the reader's own
          // credential — so it takes `secureEntry` (masking + hidden from managers), the release share-link
          // case named in the Field ruling. `type="password"` + `autoComplete="off"` does NOT suppress
          // autofill: browsers ignore `autocomplete="off"` on a password field and fill/offer-to-save anyway.
          secureEntry
          value={password}
          placeholder={token?.hasPassword ? "Replace password…" : "Add a password…"}
          onChange={(e) => setPassword(e.target.value)}
        />
        {token?.hasPassword && (
          <Button variant="ghost" size="xs" disabled={save.isPending}
            onClick={() => apply({ clearPassword: true }, "Password removed — the link is open again.")}>
            Remove password
          </Button>
        )}

        <Field
          label="Expiration (optional)"
          type="date"
          value={expiryValue}
          onChange={(e) => setExpires(e.target.value)}
        />
        {storedExpiry && (
          <Button variant="ghost" size="xs" disabled={save.isPending}
            onClick={() => { setExpires(""); apply({ clearExpiry: true }, "Expiry removed — the link has no end date."); }}>
            Remove expiry
          </Button>
        )}
      </FormDialog>

      {confirm === "rotate" && (
        <AcDialog
          title="Rotate this link?"
          message="The current url stops working the moment the new one is minted — anyone already holding it gets a 404, including venues you have already sent it to. There is no way back to the old url."
          confirmLabel="Rotate link"
          tone="danger"
          loading={rotate.isPending}
          onCancel={() => setConfirm(null)}
          onConfirm={onRotate}
        />
      )}
    </>
  );
}
